import { ChangeDetectionStrategy, Component, Input, OnChanges, inject, signal } from '@angular/core';
import { NgIf } from '@angular/common';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { OrtakService } from '../../services/ortak.service';

/**
 * Ekranın üstünde ince kırmızı deneme lisansı uyarısı (dtokgoz 11.10.2026).
 * Lisanslama sistemi gelene kadar geçici: durum GEN_RAPOR_PARAMETRE'den okunur
 * (LISANS.ANALIZ_LISANS_STATUS, LISANS.GOR_LISANS_STATUS). Değer '1' = lisanslı, bar görünmez;
 * boş/başka değer = deneme, bar görünür. Servis tanımsız parametreyi boş değerle kendisi ekler,
 * yani varsayılan "deneme"dir; istemeyen kurumda (örn. Osmaniye) değer 1 yapılır.
 * Parametre okunamazsa (servis hatası) bar gösterilmez.
 */
@Component({
  selector: 'dk-lisans-uyari',
  standalone: true,
  imports: [NgIf],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div *ngIf="metin() as m" class="lu" role="status">{{ m }}</div>
  `,
  styles: [`
    :host { display: block; flex: 0 0 auto; }
    .lu {
      background: #c62828; color: #fff;
      font: 600 12px/1.3 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      text-align: center; padding: 4px 12px;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    @media (max-width: 600px) { .lu { white-space: normal; } }
  `],
})
export class LisansUyariComponent implements OnChanges {
  /** Uyarıda görünen modül adı, örn. "Analiz Merkezi" */
  @Input({ required: true }) modulAd!: string;
  /** Lisans durumunu tutan GEN_RAPOR_PARAMETRE adı ('1' = lisanslı) */
  @Input({ required: true }) parametreAd!: string;
  /** Deneme lisansının bitiş tarihi (gg.aa.yyyy); lisanslama sistemi gelene kadar sabit */
  @Input() bitisTarihi = '31.12.2026';

  // Sekmeler arasında gidip gelince aynı parametre tekrar sorulmasın
  private static readonly onbellek = new Map<string, Observable<string | null>>();

  private readonly ortakService = inject(OrtakService);
  readonly metin = signal<string>('');

  ngOnChanges(): void {
    if (!this.parametreAd) return;
    this.durum(this.parametreAd).subscribe((d) => this.metin.set(d === null || d === '1' ? '' : this.metinUret()));
  }

  /** Parametre değeri; okunamazsa null */
  private durum(ad: string): Observable<string | null> {
    let o = LisansUyariComponent.onbellek.get(ad);
    if (!o) {
      o = this.ortakService.getParametreDeger(ad).pipe(
        map((r) => (r?.parametreDeger ?? '').trim()),
        catchError(() => of(null)), // uyarı ekranı asla bozmasın; servis yoksa bar da yok
        shareReplay(1),
      );
      LisansUyariComponent.onbellek.set(ad, o);
    }
    return o;
  }

  private metinUret(): string {
    const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(this.bitisTarihi ?? '');
    if (!m) return '';
    const t = new Date(+m[3], +m[2] - 1, +m[1]);
    const yazi = `${m[1].padStart(2, '0')}/${m[2].padStart(2, '0')}/${m[3]}`;
    const bugun = new Date();
    bugun.setHours(0, 0, 0, 0);
    return t < bugun
      ? `${this.modulAd} deneme lisansınızın süresi ${yazi} tarihinde dolmuştur.`
      : `${this.modulAd} deneme lisansınızın süresi ${yazi} tarihinde bitecektir.`;
  }
}
